import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import TripMap from "../../../components/tripMap/TripMap";
import { DayPills, DayPlaceList, MapLegend, PickupRow, StayBadge, StopBadge, TripPlanRows, stopTimes } from "../../../components/tripMap/TripMapParts";
import {
  ageLabel,
  dayDate,
  distanceLabel,
  eventTime,
  formatClock,
  formatMinutes,
  isAutoMark,
  sinceLabel,
} from "../../../components/tripMap/tripMapModel";
import { formatDateDMY, formatDateTimeDMY12 } from "../../../utils/dateFormat";

const ROUTE_WINDOWS = [
  { key: "all", label: "Whole route", minutes: null },
  { key: "1d", label: "Last 24 hr", minutes: 60 * 24 },
  { key: "4h", label: "Last 4 hr", minutes: 240 },
  { key: "1h", label: "Last 1 hr", minutes: 60 },
];

const ACTIVITY_TEXT = {
  DROPPED: (event) => `Reached ${event.placeLabel || "a place"}`,
  PICKED_UP: (event) => `Left ${event.placeLabel || "a place"}`,
  DAY_START: (event) => `${event.dayLabel || "Day"} started`,
  DAY_END: (event) => `${event.dayLabel || "Day"} ended`,
  TRIP_STARTED: () => "Trip started",
  TRIP_ENDED: () => "Trip ended",
  DRIVER_REACHED_PICKUP: () => "Driver reached pickup",
  HOTEL_NOTIFIED: (event) => `Stay told of arrival${event.placeLabel ? ` · ${event.placeLabel}` : ""}`,
};

function NoPins() {
  return (
    <div className="d-flex align-items-center justify-content-center text-center p-4" style={{ minHeight: 160, background: "#f4f6f9", color: "#5b6b80", fontSize: 13 }}>
      No map locations for this trip yet. Places get a pin from their Google Maps link in Dynamic Pricing;
      a new link shows up a minute or two after the trip is first opened.
    </div>
  );
}

function VehicleChip({ model }) {
  const vehicle = model.vehicle;
  if (model.tripEnded) return <span className="trip-status is-upcoming">Trip ended</span>;
  if (!vehicle) return <span className="trip-status is-upcoming">No location yet</span>;
  return (
    <span className={`trip-status ${vehicle.stale ? "is-upcoming" : "is-visited"}`}>
      <span className="trip-live-dot" style={vehicle.stale ? { background: "#94a3b8" } : undefined} />
      {vehicle.stale ? `Last seen ${ageLabel(vehicle.minutesAgo)}` : `Live · ${ageLabel(vehicle.minutesAgo)}`}
    </span>
  );
}

/** Transport owner view: the map with a button per day, then that day's places with their times. */
export function OwnerTripMap({ model, detail, now }) {
  const [dayFilter, setDayFilter] = useState(() => Math.max(model.currentDayIndex, 0));
  const listDays = dayFilter === "all" ? model.days : model.days.filter((day) => day.index === dayFilter);
  const shownDay = dayFilter === "all" ? null : listDays[0];

  return (
    <div className="trip-side-card p-0 overflow-hidden">
      <div className="p-3 d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div>
            <div className="trip-side-title mb-0">Trip Map</div>
            <div className="small" style={{ color: "#5b6b80" }}>
              {shownDay ? `${shownDay.label}${shownDay.title ? ` · ${shownDay.title}` : ""}` : "Whole trip"}
            </div>
          </div>
          <VehicleChip model={model} />
        </div>
        {model.days.length ? <DayPills model={model} value={dayFilter} onChange={setDayFilter} /> : null}
      </div>
      {model.hasMapContent ? <TripMap model={model} dayFilter={dayFilter} className="trip-panel-map" /> : <NoPins />}
      <div className="p-3">
        <MapLegend arrivalRadiusM={model.arrivalRadiusM} />
      </div>
      {listDays.map((day) => (
        <div key={day.key}>
          <div className="px-3 py-2 d-flex justify-content-between gap-2" style={{ background: "#f8fafc", borderTop: "1px solid #edf1f5" }}>
            <strong style={{ fontSize: 14 }}>{day.label} places</strong>
            <span style={{ fontSize: 12.5, color: "#5b6b80", fontWeight: 600 }}>{day.visitedCount} of {day.places.length} visited</span>
          </div>
          {day.index === 0 && detail ? <PickupRow detail={detail} /> : null}
          <DayPlaceList day={day} now={now} />
        </div>
      ))}
    </div>
  );
}

/** Admin view: the whole trip on one map, with where the vehicle is, each day's progress and the latest marks. */
export function AdminTripOverview({ model, detail, now }) {
  const [dayFilter, setDayFilter] = useState("all");
  const [showArrival, setShowArrival] = useState(true);
  const [showHotels, setShowHotels] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [windowKey, setWindowKey] = useState("all");
  const routeWindow = ROUTE_WINDOWS.find((item) => item.key === windowKey) || ROUTE_WINDOWS[0];

  return (
    <Row className="g-3 mb-3">
      <Col lg={8}>
        <div className="trip-overview-map">
          {model.hasMapContent ? (
            <TripMap
              model={model}
              dayFilter={dayFilter}
              showArrival={showArrival}
              showLeaveRing={showArrival}
              showHotels={showHotels}
              showRawPoints={showRaw}
              routeSinceMinutes={routeWindow.minutes}
              officeView
              stops={detail.unplannedStops || []}
              scrollWheelZoom
              fitPadding={{ topLeft: [40, 120], bottomRight: [70, 40] }}
              controlsStyle={{ top: 12 }}
            />
          ) : (
            <div className="h-100 d-flex align-items-center"><NoPins /></div>
          )}
          <div className="trip-overview-toolbar">
            <div className="trip-overview-segment">
              <button type="button" className={dayFilter === "all" ? "is-active" : ""} aria-pressed={dayFilter === "all"} onClick={() => setDayFilter("all")}>
                All days
              </button>
              {model.days.map((day) => (
                <button
                  type="button"
                  key={day.key}
                  className={dayFilter === day.index ? "is-active" : ""}
                  aria-pressed={dayFilter === day.index}
                  onClick={() => setDayFilter(day.index)}
                >
                  <span className="trip-day-dot" style={{ background: day.color }} />
                  {day.label}
                </button>
              ))}
            </div>
            <div className="trip-overview-options">
              <label><input type="checkbox" checked={showArrival} onChange={(event) => setShowArrival(event.target.checked)} /> Arrival areas</label>
              <label><input type="checkbox" checked={showHotels} onChange={(event) => setShowHotels(event.target.checked)} /> Night stays</label>
              <label><input type="checkbox" checked={showRaw} onChange={(event) => setShowRaw(event.target.checked)} /> Raw GPS points</label>
              <select aria-label="Route shown" value={windowKey} onChange={(event) => setWindowKey(event.target.value)}>
                {ROUTE_WINDOWS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </div>
          </div>
          {model.hasMapContent ? (
            <div className="trip-overview-legend">
              <MapLegend showStops arrivalRadiusM={model.arrivalRadiusM} />
              <div className="trip-legend mt-2">
                <span>
                  Circle {distanceLabel(model.arrivalRadiusM)} = arrival · dotted ring {distanceLabel(model.leaveRadiusM)} = leaving
                  {" · "}{model.dwellMinutes} min inside counts as reached
                  {model.days.some((day) => day.places.some((stop) => stop.arrivalRadiusM !== model.arrivalRadiusM))
                    ? " · places with their own circle are drawn to size"
                    : ""}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </Col>
      <Col lg={4} className="trip-overview-side">
        <NowCard model={model} detail={detail} now={now} />
        <DayProgressCard model={model} detail={detail} now={now} />
        <ActivityCard detail={detail} now={now} />
      </Col>
    </Row>
  );
}

function NowCard({ model, detail, now }) {
  const phone = String(detail.driverPhone || "").replace(/[^\d+]/g, "");
  let badge = null;
  let kicker;
  let title;
  let sub = "";
  let subWarning = false;
  if (model.tripEnded) {
    kicker = "Trip ended";
    title = formatDateTimeDMY12(detail.tripEnded?.capturedAtIst, "-");
  } else if (model.atStay) {
    badge = <StayBadge size={40} />;
    kicker = model.atStay.back ? "Back at stay" : "Reached stay";
    title = model.atStay.hotel?.name || "Tonight's stay";
    sub = sinceLabel(model.atStay.arrivedIst, now);
  } else if (model.here) {
    badge = <StopBadge stop={model.here} size={40} />;
    kicker = "Vehicle here now";
    title = model.here.label;
    sub = stopTimes(model.here, now);
  } else if (!model.tripStarted) {
    kicker = detail.driverReachedPickup ? "Driver at pickup" : "Not started";
    title = detail.driverReachedPickup
      ? `Reached ${formatClock(detail.driverReachedPickup.capturedAtIst)}`
      : `Pickup ${formatDateTimeDMY12(detail.vehiclePickupDateTime, formatDateDMY(detail.vehicleStartDate || detail.travelDate, "-"))}`;
    sub = detail.pickupPoint ? `Pickup at ${detail.pickupPoint.landmark}` : "Pickup point not set by the customer yet";
    subWarning = !detail.pickupPoint;
  } else if (model.leftStay) {
    badge = <StayBadge size={40} />;
    kicker = "Out from the stay";
    title = model.leftStay.hotel?.name || "Tonight's stay";
    sub = `Left ${formatClock(model.leftStay.leftIst)}`;
  } else {
    if (model.next) badge = <StopBadge stop={model.next} size={40} />;
    kicker = "On the way to";
    const today = model.days[Math.max(model.currentDayIndex, 0)];
    const stay = today?.tonight?.[0];
    title = model.next ? model.next.label
      : stay ? stay.name
        : today && today.index === model.days.length - 1 ? "Drop point" : "Tonight's stay";
    const last = model.lastVisited;
    sub = last ? `Left ${last.label} at ${formatClock((last.pickedUp || last.dropped)?.capturedAtIst)}` : "";
  }
  const upNext = model.tripEnded ? null : model.atStay ? model.atStay.next : model.here ? model.next : null;
  const vehicle = model.vehicle;
  const alert = detail.silenceAlert;
  return (
    <div className="trip-side-card">
      {alert ? (
        <div className="trip-silence-alert">
          <i className="bi bi-exclamation-octagon-fill" aria-hidden="true" />
          <span>
            {alert.kind === "NOT_STARTED"
              ? "Pickup time passed and the driver has not started sharing."
              : `No location since ${formatClock(alert.silentSinceIst)} (${ageLabel(Math.round((now.getTime() - eventTime({ capturedAtIst: alert.silentSinceIst })) / 60000))}).`}
            {" "}Office emailed {formatClock(alert.alertedAtIst)}.
          </span>
        </div>
      ) : null}
      <div className="d-flex align-items-center gap-3">
        {badge}
        <div style={{ minWidth: 0 }}>
          <div className="trip-now-kicker">{kicker}</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{title}</div>
          {sub ? <div style={{ fontSize: 12.5, color: subWarning ? "#b45309" : "#5b6b80", fontWeight: subWarning ? 700 : 400 }}>{sub}</div> : null}
        </div>
      </div>
      {upNext ? (
        <div className="trip-now-next mt-3">
          <i className="bi bi-arrow-right" aria-hidden="true" />
          <span><strong style={{ color: "#0f1f33" }}>Up next:</strong> {upNext.label}</span>
        </div>
      ) : null}
      <div className="d-flex justify-content-between align-items-center gap-2 mt-3 pt-2" style={{ borderTop: "1px solid #edf1f5", fontSize: 12.5 }}>
        <span style={{ color: "#33445a" }}>
          {vehicle
            ? `GPS ${formatClock(vehicle.capturedAtIst)} (${ageLabel(vehicle.minutesAgo)})`
            : "No GPS from the driver yet"}
          {detail.driverName ? <><br /><strong style={{ color: "#0f1f33" }}>{detail.driverName}</strong></> : null}
        </span>
        {phone ? (
          <a className="btn btn-sm" href={`tel:${phone}`} style={{ background: "#0f2f4a", color: "#fff", fontWeight: 700 }}>
            <i className="bi bi-telephone-fill me-1" aria-hidden="true" />Call
          </a>
        ) : null}
      </div>
    </div>
  );
}

// Each day's progress; a day opens into its places with their Reached / Left times, the same list the
// customer sees. Today (Day 1 before the trip) starts open.
function DayProgressCard({ model, detail, now }) {
  const [openDays, setOpenDays] = useState(() => new Set([Math.max(model.currentDayIndex, 0)]));
  if (!model.days.length) return null;
  const toggle = (index) => setOpenDays((previous) => {
    const next = new Set(previous);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    return next;
  });
  return (
    <div className="trip-side-card p-0 overflow-hidden">
      <div className="trip-side-title px-3 pt-3 mb-2">Day by day</div>
      {model.days.map((day) => {
        const percent = day.places.length ? Math.round((day.visitedCount / day.places.length) * 100) : 0;
        const date = dayDate(detail.travelDate, day.index);
        const open = openDays.has(day.index);
        return (
          <div key={day.key}>
            <button type="button" className="trip-day-toggle" aria-expanded={open} onClick={() => toggle(day.index)}>
              <div className="d-flex justify-content-between gap-2" style={{ fontSize: 13, fontWeight: 700 }}>
                <span className="d-inline-flex align-items-center gap-2">
                  <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
                  <span className="trip-day-dot" style={{ background: day.color }} />
                  {day.label}{date ? ` · ${date}` : ""}
                  {day.index === model.currentDayIndex && !model.tripEnded ? <span style={{ color: "#1d4ed8" }}>· Today</span> : null}
                </span>
                <span style={{ color: "#5b6b80", whiteSpace: "nowrap" }}>
                  {day.visitedCount} / {day.places.length}
                  {day.missedCount ? <span style={{ color: "#9f1d1d" }}> · {day.missedCount} not visited</span> : null}
                </span>
              </div>
              {day.title ? <div className="text-truncate" style={{ fontSize: 12, color: "#5b6b80", marginTop: 2 }}>{day.title}</div> : null}
              <div className="trip-progress" style={{ height: 5, marginTop: 6 }}>
                <span style={{ width: `${percent}%`, background: day.color }} />
              </div>
              {!open && day.tonight.length ? (
                <div style={{ fontSize: 12, color: "#5b6b80", marginTop: 4 }}>
                  {day.tonight.map((hotel) => `${hotel.name}`).join(", ")}
                </div>
              ) : null}
            </button>
            {open ? <TripPlanRows detail={detail} model={model} day={day} now={now} office /> : null}
          </div>
        );
      })}
    </div>
  );
}

// Marks and unplanned stops together, newest first. Meals are no longer marked; a stop away from
// the plan (lunch, fuel, a breakdown) shows here instead.
function ActivityCard({ detail, now }) {
  const marks = (detail.events || [])
    .filter((event) => ACTIVITY_TEXT[event.eventType])
    .map((event) => ({ key: event.id || `${event.eventType}-${event.placeKey}`, at: event.capturedAtIst, event }));
  const stops = (detail.unplannedStops || [])
    .map((stop) => ({ key: `stop-${stop.fromIst}`, at: stop.fromIst, stop }));
  const rows = [...marks, ...stops]
    .sort((a, b) => eventTime({ capturedAtIst: b.at }) - eventTime({ capturedAtIst: a.at }))
    .slice(0, 10);
  return (
    <div className="trip-side-card">
      <div className="d-flex justify-content-between align-items-baseline">
        <div className="trip-side-title">Activity</div>
        <span style={{ fontSize: 12, color: "#5b6b80" }}>Newest first</span>
      </div>
      {rows.length ? rows.map((row) => (
        <div className="trip-activity-row" key={row.key}>
          <span className="trip-activity-time">{activityTime(row.at, now)}</span>
          {row.stop ? (
            <span style={{ flex: 1, minWidth: 0 }}>
              Stopped {formatMinutes(row.stop.minutes)} away from the plan{" "}
              <a href={`https://www.google.com/maps/search/?api=1&query=${row.stop.latitude},${row.stop.longitude}`} target="_blank" rel="noopener noreferrer">map</a>
            </span>
          ) : (
            <span style={{ flex: 1, minWidth: 0 }}>{ACTIVITY_TEXT[row.event.eventType](row.event)}</span>
          )}
          {row.event && isAutoMark(row.event) ? <span className="trip-status is-auto" title="Marked from the route, not tapped">Auto</span> : null}
          {row.event && !isAutoMark(row.event) && row.event.latitude == null ? (
            <span className="trip-status is-upcoming" title="No GPS with this mark">No GPS</span>
          ) : null}
        </div>
      )) : (
        <div style={{ fontSize: 13, color: "#5b6b80" }}>Nothing marked yet.</div>
      )}
    </div>
  );
}

function activityTime(value, now) {
  const time = eventTime({ capturedAtIst: value });
  if (!time) return "-";
  const date = new Date(time);
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return formatClock(value);
  return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${formatClock(value)}`;
}
